<script setup lang="ts">
import type { Game } from '@game/types'
import { vIntersectionObserver } from '@vueuse/components'
import { computed, watch, useGameAssets, useGameConfigurationClient, useGameList, useApm, useTileVisibility } from '#imports'

export interface NimbusBlogGameListProps {
    slugs?: string
    video?: boolean
}

const props = defineProps<NimbusBlogGameListProps>()

const apm = useApm()
const { getGamesBySlugs } = useGameConfigurationClient()
const { isVideoTile } = useGameAssets()

const videoEnabled = computed(() => props.video === true)

const slugList = computed(() =>
    (props.slugs ?? '').split(',').map(slug => slug.trim()).filter(Boolean))

const query = getGamesBySlugs(slugList, { server: true })

// Search payload fields are all optional and omit id/headline/description/jackpot.
const games = computed<Game[]>(() => (query.data.value ?? []) as unknown as Game[])

useGameList(() => games.value)

const { active: mediaTiles, onTileVisible, observerOptions } = useTileVisibility()

const settledWithMissing = computed(() =>
    query.status.value !== 'pending' && games.value.length < slugList.value.length)

watch(settledWithMissing, (missing) => {
    if (missing) {
        apm.captureError(
            new Error(`Blog game-list: ${slugList.value.length - games.value.length} unresolvable slug(s)`),
            { labels: { slugs: slugList.value.join(',') } },
        )
    }
}, { immediate: true })
</script>

<template>
    <div v-if="games.length" class="blog-game-list">
        <GameTile
            v-for="game in games"
            :key="game.slug"
            v-intersection-observer="[onTileVisible, observerOptions]"
            :data-tile-slug="game.slug"
            :class="{ 'blog-game-list__tile--video': isVideoTile(game, videoEnabled) }"
            :game="game"
            :video-enabled="videoEnabled"
            :load-media="mediaTiles.has(game.slug)"
            is-inline
        />
    </div>
</template>

<style scoped lang="scss">
// Inline GameTile is hard-set to 135/160px — flexible columns leave empty space.
.blog-game-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, 135px);
    grid-auto-rows: 135px;
    gap: 12px;
    margin: 1rem 0;

    @include desktop {
        grid-template-columns: repeat(auto-fill, 160px);
        grid-auto-rows: 160px;
    }

    // The tile withholds its own --video class when disabled; span from the game config instead.
    &__tile--video {
        grid-column: span 2;
    }
}
</style>
